import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./VideoInsights.css";
import Header from "../../Components/Header/Header";
import likeIcon from "../../assets/images/LikeIcon.png";
import shareIcon from "../../assets/images/forward.png";
import saveIcon from "../../assets/images/SaveIcon.png";
import InfoIcon from "../../assets/images/InfoIcon.png";
import formatPublishedDate, {
  formatDurationSeconds,
  formatWatchTime,
} from "../../utils/helpers";
import { callApi } from "../../utils/apiService";
import { videoCreatorEndPoint } from "../../Constants/constants";

const VideoInsights = () => {
  const navigate = useNavigate();
  const videoRef = useRef(null);

  const [activeInsightsTab, setActiveInsightsTab] = useState("Overview");

  // Tracks which info tooltip is currently open. null = none open.
  const [openTooltip, setOpenTooltip] = useState(null);
  const [insightsData, setInsightsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const location = useLocation();

  const selectedContent = location?.state.item;
  const selectedType = location?.state.item?.contentType ?? location?.state.contentType;; // change to Reel / Story

  const fetchInsights = async () => {
    try {
      setLoading(true);

      const contentType =
        selectedType?.toLowerCase() === "reel"
          ? "reel"
          : "video";

      const response = await callApi(
        `${videoCreatorEndPoint}${contentType}/${selectedContent?.id || selectedContent?.videoId}/insights`,
        "GET",
      );

      if (response?.status) {
        setInsightsData(response?.data?.videoInsights);
      }
    } catch (error) {
      console.error("Insights API Error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedContent?.id ?? selectedContent?.videoId) {
      fetchInsights();
    }
  }, [selectedContent?.id, selectedContent?.videoId]);

  // Tooltip copy for each info icon
  const tooltipContent = {
    impressions:
      "Impressions show how many times your content appeared on users' screens, whether they interacted with it or not.",
    watchTime:
      "Total Watch Time is the combined time viewers spent watching your content. Longer watch time indicates higher audience engagement.",
    consumption:
      "Consumption Quality measures how well viewers watched your content based on completion rate, watch time, and replays. Higher quality means stronger viewer engagement.",
    momentum:
      "Momentum & Velocity measures how quickly your content gains views and engagement after it is published. Faster growth increases its chances of reaching more people.",
  };

  const toggleTooltip = (key) => {
    setOpenTooltip((prev) => (prev === key ? null : key));
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Close any open tooltip when clicking outside of it
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".metric-info-wrapper")) {
        setOpenTooltip(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!selectedContent) {
    return (
      <div>
        <Header />
        <div className="insights-container">
          <button className="back-button" onClick={() => navigate(-1)}>
            ← Back
          </button>
          <p>No content selected. Please select a content item first.</p>
        </div>
      </div>
    );
  }

  const getInsightsTitle = () => {
    const typeMap = {
      Video: "Video Insights",
      Reel: "Reel Insights",
      Story: "Story Insights",
    };
    return typeMap[selectedType] || "Video Insights";
  };

  const videoUrl =
    selectedContent?.videoDownloadUrl &&
      selectedType === "Video"
      ? encodeURI(selectedContent.videoDownloadUrl)
      : selectedContent?.reelDownloadUrl &&
        selectedType === "Reel"
        ? encodeURI(selectedContent.reelDownloadUrl)
        : "";

  return (
    <>
      <Header />
      <div className="insights-container">
        <div className="insights-header">
          <h1 className="insights-page-title">{getInsightsTitle()}</h1>
          <p className="insights-update-time">Last updated 2 hours ago</p>
        </div>

        <div className="insights-breadcrumb">
          <span className="breadcrumb-link">Content Library</span>
          <span className="breadcrumb-separator">›</span>
          <span className="breadcrumb-link">
            {selectedType}s
          </span>
          <span className="breadcrumb-separator">›</span>
          <span className="breadcrumb-current">Insights</span>
        </div>

        <div className="insights-video-section">
          <div className="video-player-container">
            <video
              className="video-player"
              src={videoUrl}
              controls
              playsInline
              preload="metadata"
              poster={
                selectedType === "Video"
                  ? selectedContent?.thumbnailImage
                  : selectedType === "Story"
                    ? selectedContent?.thumbnailImage ||
                    selectedContent?.storyUrl
                    : selectedContent?.generatedThumbnail
              }
              onError={(e) => {
                console.error("Video failed to load:", videoUrl, e);
              }}
            />
          </div>

          <div className="insights-video-info">
            <h2 className="insights-video-title">
              {selectedContent?.title ?? selectedContent?.caption}
            </h2>
            <p className="insights-video-duration">{`${selectedType === "Video" ? formatDurationSeconds(selectedContent.videoDuration) : selectedType === "Story" ? formatDurationSeconds(selectedContent?.storyDuration) : formatDurationSeconds(selectedContent?.reelDuration)}`}</p>
            <p className="insights-publish-info">
              Published on {formatPublishedDate(selectedContent.publishedAt)}
            </p>
          </div>
        </div>

        <div className="insights-tabs-container">
          <div className="insights-tabs">
            <button
              className={`insights-tab ${activeInsightsTab === "Overview" ? "active" : ""}`}
              onClick={() => setActiveInsightsTab("Overview")}
            >
              Overview
            </button>
            <button
              className={`insights-tab ${activeInsightsTab === "Engagement" ? "active" : ""}`}
              onClick={() => setActiveInsightsTab("Engagement")}
            >
              Engagement
            </button>
          </div>

          <div className="insights-tab-content">
            {activeInsightsTab === "Overview" && (
              <>
                <div className="insights-metrics-grid">
                  <div className="metric-card">
                    <div className="metric-label">
                      <p>Impressions</p>
                      <span className="metric-info-wrapper">
                        <img
                          src={InfoIcon}
                          alt="Impressions info"
                          className="info-metric-icon-img"
                          onClick={() => toggleTooltip("impressions")}
                        />
                        {openTooltip === "impressions" && (
                          <div className="info-tooltip-popup">
                            {tooltipContent.impressions}
                          </div>
                        )}
                      </span>
                    </div>
                    <div className="metric-value">
                      {insightsData?.impressionCount || 0}
                    </div>
                  </div>
                  <div className="metric-card">
                    <div className="metric-label">
                      Total Watch Time
                      <span className="metric-info-wrapper">
                        <img
                          src={InfoIcon}
                          alt="Total Watch Time info"
                          className="info-metric-icon-img"
                          onClick={() => toggleTooltip("watchTime")}
                        />
                        {openTooltip === "watchTime" && (
                          <div className="info-tooltip-popup">
                            {tooltipContent.watchTime}
                          </div>
                        )}
                      </span>
                    </div>

                    <div className="metric-value">
                      {formatWatchTime(insightsData?.totalWatchDuration || 0)}
                    </div>
                  </div>
                </div>

                <div className="engagement-section">
                  <h3 className="section-title">Intent & Engagement</h3>

                  <div className="engagement-metrics">
                    {/* SAVES */}
                    <div className="engagement-item">
                      <div className="engagement-stat">
                        <div className="engagement-left">
                          <img
                            src={saveIcon}
                            alt="saves"
                            className="engagement-icon-img"
                          />
                          <span className="engagement-label">Saves</span>
                        </div>

                        <div className="engagement-number">
                          {insightsData?.totalSaves || 0}
                        </div>
                      </div>

                      <div className="engagement-bar">
                        <div
                          className="engagement-bar-fill"
                          style={{
                            width: `${(insightsData?.totalSaves / 250) * 100}%`,
                            backgroundColor: "#474448",
                          }}
                        ></div>
                      </div>
                    </div>

                    {/* LIKES */}
                    <div className="engagement-item">
                      <div className="engagement-stat">
                        <div className="engagement-left">
                          <img
                            src={likeIcon}
                            alt="likes"
                            className="engagement-icon-img"
                          />
                          <span className="engagement-label">Likes</span>
                        </div>

                        <div className="engagement-number">
                          {insightsData?.totalLikes || 0}
                        </div>
                      </div>

                      <div className="engagement-bar">
                        <div
                          className="engagement-bar-fill"
                          style={{
                            width: `${(insightsData?.totalLikes / 250) * 100}%`,
                            backgroundColor: "#474448",
                          }}
                        ></div>
                      </div>
                    </div>

                    {/* SHARES */}
                    <div className="engagement-item">
                      <div className="engagement-stat">
                        <div className="engagement-left">
                          <img
                            src={shareIcon}
                            alt="shares"
                            className="engagement-icon-img"
                          />
                          <span className="engagement-label">Shares</span>
                        </div>

                        <div className="engagement-number">
                          {insightsData?.totalShares || 0}
                        </div>
                      </div>

                      <div className="engagement-bar">
                        <div
                          className="engagement-bar-fill"
                          style={{
                            width: `${(insightsData?.totalShares / 250) * 100}%`,
                            backgroundColor: "#474448",
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {activeInsightsTab === "Engagement" && (
              <>
                <div className="consumption-section">
                  <div className="consumption-header">
                    <h3 className="section-title">Consumption Quality</h3>
                    <span className="metric-info-wrapper">
                      <img
                        src={InfoIcon}
                        alt="Consumption Quality info"
                        className="info-icon-img"
                        onClick={() => toggleTooltip("consumption")}
                      />
                      {openTooltip === "consumption" && (
                        <div className="info-tooltip-popup">
                          {tooltipContent.consumption}
                        </div>
                      )}
                    </span>
                  </div>

                  <div className="consumption-metrics">
                    <div className="consumption-metric">
                      <div className="metric-header">TOTAL VIEWS</div>
                      <div className="metric-content">
                        <span className="metric-main-value">
                          {insightsData?.totalViews || 0}
                        </span>
                      </div>
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{
                            width: `${insightsData?.totalViews / 2}%`,
                          }}
                        ></div>
                      </div>
                    </div>
                    <div className="consumption-metric">
                      <div className="metric-header">AVG COMPLETION</div>
                      <div className="metric-content">
                        <span className="metric-main-value">
                          {formatWatchTime(insightsData?.avgCompletionRate) ||
                            0}
                        </span>
                        <span
                          style={{ paddingLeft: "6px" }}
                          className="metric-sub-value"
                        >
                          avg watch (out of 11 min)
                        </span>
                      </div>
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{
                            width: `${insightsData?.avgCompletionRate}%`,
                          }}
                        ></div>
                      </div>
                    </div>

                    <div className="consumption-metric">
                      <div className="metric-header">90%+COMPLETION</div>
                      <div className="metric-content">
                        <span className="metric-main-value">
                          {insightsData?.completed90Count || 0}
                          <span
                            style={{ paddingLeft: "6px" }}
                            className="metric-sub-value"
                          >
                            users
                          </span>
                        </span>
                      </div>
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{
                            width: `${insightsData?.completed90Count}%`,
                          }}
                        ></div>
                      </div>
                    </div>

                    <div className="consumption-metric">
                      <div className="metric-header">EARLY DROP-OFF</div>
                      <div className="metric-content">
                        <span className="metric-main-value">
                          {insightsData?.earlyDropOffCount || 0}
                          <span
                            style={{ paddingLeft: "6px" }}
                            className="metric-sub-value"
                          >
                            users
                          </span>
                        </span>
                      </div>
                      <div className="progress-bar">
                        <div
                          className="progress-fill red-fill"
                          style={{
                            width: `${insightsData?.earlyDropOffCount}%`,
                          }}
                        ></div>
                      </div>
                    </div>

                    <div className="consumption-metric">
                      <div className="metric-header">REPLAYS</div>
                      <div className="metric-content">
                        <span className="metric-main-value">
                          {insightsData?.replayCount || 0}
                          <span
                            style={{ paddingLeft: "6px" }}
                            className="metric-sub-value"
                          >
                            users / replays
                          </span>
                        </span>
                      </div>
                      <div className="progress-bar">
                        <div
                          className="progress-fill green-fill"
                          style={{
                            width: `${insightsData?.replayCount}%`,
                          }}
                        ></div>
                      </div>
                    </div>
                    <div className="consumption-metric">
                      <div className="metric-header">
                        CTR{" "}
                        <span className="ctr-header">
                          {" "}
                          (👉 % of people who clicked after seeing your
                          thumbnail)
                        </span>
                      </div>
                      <div className="metric-content">
                        <span className="metric-main-value">
                          {insightsData?.ctr || 0}%
                        </span>
                      </div>
                      <div className="progress-bar">
                        <div
                          className="progress-fill green-fill"
                          style={{
                            width: `${insightsData?.ctr}%`,
                          }}
                        ></div>
                      </div>
                    </div>

                    <div className="consumption-metric">
                      <div className="metric-header">
                        AVG WATCH TIME / AVG WATCH TIME %{" "}
                      </div>
                      <div className="metric-content">
                        <span className="metric-main-value">
                          {formatWatchTime(insightsData?.avgCompletionRate) ||
                            0}{" "}
                          /
                          <span style={{ paddingLeft: "5px" }}>
                            {insightsData?.avgCompletionPercentage || 0}
                            {"%"}
                          </span>
                        </span>
                      </div>
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{
                            width: `${insightsData?.avgCompletionPercentage}%`,
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="momentum-section">
                  <div className="momentum-header">
                    <h3 className="section-title">Momentum & Velocity</h3>
                    <span className="metric-info-wrapper">
                      <img
                        src={InfoIcon}
                        alt="Momentum & Velocity info"
                        className="info-icon-img"
                        onClick={() => toggleTooltip("momentum")}
                      />
                      {openTooltip === "momentum" && (
                        <div className="info-tooltip-popup">
                          {tooltipContent.momentum}
                        </div>
                      )}
                    </span>
                  </div>

                  <div className="momentum-metrics">
                    <div className="momentum-card">
                      <div className="momentum-label">COMPLETION VELOCITY</div>
                      <div className="momentum-value">
                        {insightsData?.avgCompletionPercentage || 0}
                      </div>
                    </div>
                    <div className="momentum-card">
                      <div className="momentum-label">VIEW VELOCITY</div>
                      <div className="momentum-value">
                        {insightsData?.viewVelocity || 0}
                      </div>
                    </div>
                    <div className="momentum-card">
                      <div className="momentum-label">SHARE RATE</div>
                      <div className="momentum-value">
                        {insightsData?.shareRate || 0}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default VideoInsights;
